import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
    Container,
    Row,
    Card,
    Modal,
    ListGroup,
    ListGroupItem,
    Badge,
} from "reactstrap"
import DataTable from "react-data-table-component"
import {
    getActiveOrders,
    getRidersByZone,
    subscriptionOrder,
    updateOrderStatus,
    assignRider,
    acceptScheduledOrder
} from "../../apollo/server"
import Header from "../../components/Headers/Header"
import CustomLoader from "../../components/Loader/CustomLoader"
import { paginationComponentOptions } from "../../variables/table"
import { convertOrderStatus } from "../../utils/convertOrderStatus"
import { errorHandler } from "../../utils/errorHandler"
import {
    useQuery, useMutation,
    useSubscription,
    gql,
} from "@apollo/client"
import Button from "reactstrap/lib/Button"
import OrderComponent from "../../components/Order/Order"
import { dateToJapanese } from "../../utils/date"

// 予約(SCHEDULED)は配達時刻のリードタイム前に自動でPENDINGへ解放される。
// 解放前は配車・ステータス操作の対象外なので、板では「予約」として区別して表示する。
const isScheduled = row => row.orderStatus === "SCHEDULED"

// API から来る日時は ISO 文字列だが、過去データ互換で epoch ミリ秒の文字列もあり得る。
const parseDate = (raw: string | null | undefined): Date | null => {
    if (!raw) return null
    const d = new Date(isNaN(Number(raw)) ? raw : Number(raw))
    return isNaN(d.getTime()) ? null : d
}

// 店舗が調理に入れる(＝配車が始まる)時刻。API の releaseAt をそのまま使う。
const releaseAtOf = row => parseDate(row.releaseAt)

const timeLabel = (d: Date) => `${("" + d.getHours()).padStart(2, "0")}:${("" + d.getMinutes()).padStart(2, "0")}`

const Orders: React.FC = () => {
    const { t } = useTranslation()

    const [orderStatusModal, setOrderStatusModal] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [showOrderDetailModal, setOrderDetailModal] = useState(false)
    function toggleOrderDetailModal(order) {
        if (order) setSelectedOrder(order)
        setOrderDetailModal(prev => !prev)
    }

    const OrderInformationCell = props => {
        const { row } = props
        useSubscription(gql` ${subscriptionOrder} `, {
            variables: { id: row._id }
        })
        return <div>
            <p className="mb-0 text-sm">
                {isScheduled(row) && <Badge color="warning" className="mr-1">予約</Badge>}
                {row.orderId}
            </p>
            <p className="mb-0 text-sm">{row.deliveryAddress.deliveryAddress}</p>
            {row.memo && <p className="mb-0 text-sm text-muted">連絡事項：{row.memo}</p>}
        </div>
    }

    const OrderStatus = row => {
        const [mutateAcceptScheduled, { loading: accepting }] = useMutation(gql`${acceptScheduledOrder}`, {
            onError: e => window.alert(errorHandler(e, t)),
            refetchQueries: [{ query: gql`${getActiveOrders}` }]
        })
        const [mutateUpdateStatus] = useMutation(gql`${updateOrderStatus}`, {
            onError: e => {
                window.alert(errorHandler(e, t))
                setOrderStatusModal(false)
            },
            onCompleted: () => {
                setOrderStatusModal(false)
            },
            refetchQueries: [{ query: gql`${getActiveOrders}` }]
        })

        function onPress(e, row) {
            e.preventDefault()
            setOrderStatusModal(true)
            setSelectedOrder(row)
        }

        const OrderStatusModal = () => {
            function onPress(e, orderStatus) {
                mutateUpdateStatus({
                    variables: { id: selectedOrder._id, status: orderStatus, reason: "" }
                })
            }

            return (
                <ListGroup style={{ cursor: "pointer" }}>
                    {selectedOrder.orderStatus === "PENDING" &&
                        <ListGroupItem onClick={e => onPress(e, "ACCEPTED")} >
                            {convertOrderStatus("ACCEPTED")}に変更する
                        </ListGroupItem>
                    }

                    {["SCHEDULED", "PENDING", "ACCEPTED", "PICKED", "READY_TO_PICK"].includes(selectedOrder.orderStatus) &&
                        <>
                            <ListGroupItem onClick={e => onPress(e, "CANCELLED_BY_USER")}>
                                注文を{convertOrderStatus("CANCELLED")}する（客不出）
                            </ListGroupItem>
                            <ListGroupItem onClick={e => onPress(e, "CANCELLED")}>
                                注文を{convertOrderStatus("CANCELLED")}する
                            </ListGroupItem>
                        </>
                    }

                    {["ACCEPTED"].includes(selectedOrder.orderStatus) &&
                        <ListGroupItem onClick={e => onPress(e, "READY_TO_PICK")} >
                            {convertOrderStatus("READY_TO_PICK")}に変更する
                        </ListGroupItem>
                    }

                    {["READY_TO_PICK"].includes(selectedOrder.orderStatus) &&
                        <ListGroupItem
                            onClick={e => {
                                if (!selectedOrder.rider) {
                                    alert(t("ShouldSelectRider"))
                                    return
                                }
                                onPress(e, "PICKED")
                            }}
                        >
                            {convertOrderStatus("PICKED")}に変更する
                        </ListGroupItem>
                    }

                    {["PICKED"].includes(selectedOrder.orderStatus) &&
                        <ListGroupItem onClick={e => onPress(e, "DELIVERED")} >
                            {convertOrderStatus("DELIVERED")}に変更する
                        </ListGroupItem>
                    }
                </ListGroup>
            )
        }

        // 予約は解放前でも店舗が「事前受付」できる。準備中への遷移だけは解放ジョブに任せる
        // （手動で準備中にすると当日の調理が早く始まってしまうため）。
        if (isScheduled(row)) {
            const release = releaseAtOf(row)
            const accepted = !!row.scheduledAcceptedAt
            return (
                <div>
                    <Badge color={accepted ? "success" : "warning"} pill>
                        {accepted ? "受付済み" : "配達時間待ち"}
                    </Badge>
                    {release && (
                        <p className="mb-0 mt-1 text-xs text-muted">
                            {timeLabel(release)} 受付開始
                        </p>
                    )}
                    {!accepted && (
                        <Button
                            color="primary"
                            className="btn-block mt-1"
                            size="sm"
                            disabled={accepting}
                            onClick={e => {
                                e.preventDefault()
                                if (!window.confirm(`注文 ${row.orderId} を受け付けます。よろしいですか？`)) return
                                mutateAcceptScheduled({ variables: { id: row._id } })
                            }}
                        >
                            予約を受け付ける
                        </Button>
                    )}
                    <Button
                        color="secondary"
                        className="btn-block mt-1"
                        size="sm"
                        onClick={e => onPress(e, row)}
                    >
                        キャンセル
                    </Button>
                    <Modal
                        className="modal-dialog-centered"
                        size="sm"
                        isOpen={orderStatusModal}
                        style={{ width: 300 }}
                        toggle={() => { setOrderStatusModal(prev => !prev) }}
                    >
                        <OrderStatusModal />
                    </Modal>
                </div>
            )
        }

        return (
            <div>
                {!["CANCELLED", "DELIVERED"].includes(row.orderStatus) && (
                    <Button
                        color="secondary"
                        className="btn-block"
                        onClick={e => onPress(e, row)}
                        size="sm"
                    >
                        {convertOrderStatus(row.orderStatus)}
                    </Button>
                )}
                <Modal
                    className="modal-dialog-centered"
                    size="sm"
                    isOpen={orderStatusModal}
                    style={{ width: 300 }}
                    toggle={() => { setOrderStatusModal(prev => !prev) }}
                >
                    <OrderStatusModal />
                </Modal>
            </div>
        )
    }


    const Rider = row => {
        function onPress(e, row) {
            e.preventDefault()
            setSelectedOrder(row)
            setOpenRiderModal(true)
        }

        const RiderModal = () => {
            const [assign] = useMutation(gql` ${assignRider} `, {
                onCompleted: () => {
                    setOpenRiderModal(false)
                },
                onError: e => {
                    window.alert(errorHandler(e, t))
                    setOpenRiderModal(false)
                },
                refetchQueries: [{ query: gql`${getActiveOrders}` }]
            })
            const { loading, error, data, } = useQuery(gql` ${getRidersByZone} `, {
                variables: { id: selectedOrder.zone._id }
            })
            if (loading) return null
            if (error) {
                console.log(error)
                return null
            }

            function onPress(e, riderId) {
                e.preventDefault()
                assign({ variables: { id: selectedOrder._id, riderId: riderId } })
            }

            const selected = (riderId: string) => selectedOrder.rider && selectedOrder.rider._id === riderId

            return (
                <ListGroup>
                    {data && data.ridersByZone && data.ridersByZone.map(rider => (
                        <ListGroupItem
                            key={rider._id}
                            onClick={e => onPress(e, rider._id)}
                            style={{ cursor: "pointer" }}
                        >
                            {selected(rider._id) ? rider.name + "を解除" : rider.name}
                        </ListGroupItem>
                    ))
                    }
                </ListGroup>
            )
        }

        // 予約は解放時に自動で配車通知が飛ぶため、解放前の割当はさせない
        if (isScheduled(row)) return <>-</>

        return (
            <div>
                {!["CANCELLED", "DELIVERED"].includes(row.orderStatus) && (
                    <Button
                        color="secondary"
                        className="btn-block"
                        onClick={e => onPress(e, row)}
                        size="sm"
                    >
                        {row.rider ? row.rider.name : t("NotSelected")}
                    </Button>
                )}
                <Modal
                    className="modal-dialog-centered"
                    size="sm"
                    isOpen={openRiderModal}
                    style={{ width: 200 }}
                    toggle={() => { setOpenRiderModal(prev => !prev) }}
                >
                    <RiderModal />
                </Modal>
            </div>
        )
    }

    const CreatedAt = row => <>{dateToJapanese(new Date(row.createdAt))}</>

    // 配達希望日時。scheduledTime は epoch ミリ秒の文字列で返る。
    const ScheduledAt = row => {
        if (!row.scheduledTime) return <span className="text-muted">指定なし</span>
        const raw = row.scheduledTime
        const d = new Date(isNaN(Number(raw)) ? raw : Number(raw))
        if (isNaN(d.getTime())) return <span className="text-muted">-</span>
        return <span className="text-danger font-weight-bold">{dateToJapanese(d)}</span>
    }

    const columns = [
        {
            name: t("OrderInformation"),
            sortable: true,
            selector: "orderId",
            // maxWidth: 500,
            cell: row => <OrderInformationCell row={row} />
        },
        {
            name: t("Restaurant"),
            selector: "restaurant.name",
            // maxWidth: 200,
        },
        {
            name: t("Status"),
            selector: "orderStatus",
            cell: OrderStatus,
            maxWidth: "150px",
        },
        {
            name: t("Rider"),
            selector: "rider",
            cell: Rider,
            maxWidth: "150px",
        },
        {
            name: "配達希望日時",
            cell: ScheduledAt,
            maxWidth: "200px",
        },
        {
            name: t("OrderTime"),
            cell: CreatedAt,
            maxWidth: "200px",
        }
    ]



    const [openRiderModal, setOpenRiderModal] = useState(false)

    const conditionalRowStyles = [
        {
            when: row => ["DELIVERED", "CANCELLED"].includes(row.orderStatus),
            style: { backgroundColor: "#FDEFDD" }
        },
        {
            when: isScheduled,
            style: { backgroundColor: "#FFF8E1" }
        }
    ]

    const { loading, error, data, } = useQuery(gql`${getActiveOrders}`, {
        pollInterval: 30000
    })
    if (error) {
        console.log(error)
        return (
            <tr>
                <td>
                    {`${t("Error")}! ${error.message}`}
                </td>
            </tr>
        )
    }
    return (
        <>
            <Header />
            {/* Page content */}
            <Container className="mt--7" fluid>
                {/* Table */}
                <Row>
                    <div className="col">
                        <Card className="shadow">
                            <DataTable
                                title={t("Dispatch")}
                                columns={columns}
                                onRowClicked={toggleOrderDetailModal}
                                data={data && data.getActiveOrders ? data.getActiveOrders : []}
                                progressPending={loading}
                                pointerOnHover
                                progressComponent={<CustomLoader />}
                                pagination
                                conditionalRowStyles={conditionalRowStyles}
                                paginationComponentOptions={paginationComponentOptions}
                                noDataComponent={t("NoData")}
                            />
                        </Card>
                    </div>
                </Row>
            </Container>
            <Modal
                className="modal-dialog-centered"
                size="lg"
                isOpen={selectedOrder && showOrderDetailModal}
                toggle={() => toggleOrderDetailModal(null)}>
                <OrderComponent order={selectedOrder} />
            </Modal>
        </>
    )
}

export default Orders
